// src/pages/InsuranceRegister.js
import React, { useEffect, useState, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";
import { useFormInput } from "@/utils";
import { Confirm, Loading, Toast } from "@/components";
import SearchAddress from "@/components/Modal/Common/SearchAddress";

import { orderTypeOptions } from "@/constants/selectOptions";
import {
    useGetMyRentCorpQuery,
    useGetInsuranceConfigDetailQuery,
    useUpdateInsuranceConfigMutation,
    useDeleteInsuranceConfigMutation,
    useCreateInsuranceConfigMutation,
} from "@/redux/rentCorpApi";

import {
    emergencyServiceOptions,
    daeinPay1Options,
    daeinPay2Options,
    daemulPayOptions,
    jasonOptions,
    selfCarDamageTypeOptions,
    selfCarDamageAmountOptions,
    selfCarDamageRateOptions,
    noInsInjuryOptions,
} from "@/constants/selectOptions";

const INSURANCE_ITEM_CONFIGS = {
    DAEIN_PAY_1: {
        label: "대인 배상 I",
        coverageOptions: daeinPay1Options,
    },
    DAEIN_PAY_2: {
        label: "대인 배상 II",
        coverageOptions: daeinPay2Options,
    },
    DAEMUL_PAY: {
        label: "대물 배상",
        coverageOptions: daemulPayOptions,
    },
    JASON_DEAD: {
        label: "자동차상해(사망)",
        coverageOptions: jasonOptions,
    },
    JASON_INJURY: {
        label: "자동차상해(부상)",
        coverageOptions: jasonOptions,
    },
    JASON_BLOCK: {
        label: "자동차상해(장해)",
        coverageOptions: jasonOptions,
    },
    SELF_CAR_DAMAGE: {
        label: "자기차량손해",
        // 첫 번째 select: '금액'/'비율'
        // 두 번째 select는 첫 번째 선택값에 따라 selfCarDamageAmountOptions / selfCarDamageRateOptions
    },
    NO_INS_INJURY: {
        label: "무보험차상해",
        coverageOptions: noInsInjuryOptions,
    },
    EMERGENCY_SERVICE: {
        label: "긴급출동",
        coverageOptions: emergencyServiceOptions,
    },
};

export const InsuranceRegister = () => {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { state } = location;

    const { formData, handleChange, setFormData } = useFormInput({
        mode: "CREATE", // "CREATE" / "UPDATE"
        toastMsg: "",
        confirmStatus: {
            confirmPopupStatus: false,
            confirmTitle: "",
            confirmDescription: "",
            confirmButtons: [],
        },
        id: null,
        rentCorpId: state?.rentCorpId || null,

        // 보험 필드
        name: "",
        defaultYn: "N",
        useYn: "Y",
        contractTypes: [],
        insuranceConfigItems: [
            {
                insuranceItemType: "DAEIN_PAY_1",
                joinYn: "N",
                limitType: null,
                compensationRange: null,
                selfPay: null,
            },
            {
                insuranceItemType: "DAEIN_PAY_2",
                joinYn: "N",
                limitType: null,
                compensationRange: null,
                selfPay: null,
            },
            {
                insuranceItemType: "DAEMUL_PAY",
                joinYn: "N",
                limitType: null,
                compensationRange: null,
                selfPay: null,
            },
            {
                insuranceItemType: "NO_INS_INJURY",
                joinYn: "N",
                limitType: null,
                compensationRange: null,
                selfPay: null,
            },
            {
                insuranceItemType: "JASON_DEAD",
                joinYn: "N",
                limitType: null,
                compensationRange: null,
                selfPay: null,
            },
            {
                insuranceItemType: "JASON_INJURY",
                joinYn: "N",
                limitType: null,
                compensationRange: null,
                selfPay: null,
            },
            {
                insuranceItemType: "JASON_BLOCK",
                joinYn: "N",
                limitType: null,
                compensationRange: null,
                selfPay: null,
            },
            {
                insuranceItemType: "SELF_CAR_DAMAGE",
                joinYn: "N",
                limitType: null,
                compensationRange: null,
                selfPay: null,
                selfCarDamageRangeType: null,
                selfCarDamageRateType: null,
            },
            {
                insuranceItemType: "EMERGENCY_SERVICE",
                joinYn: "N",
                limitType: null,
                compensationRange: null,
                selfPay: null,
            },
        ],
    });

    const { mode, toastMsg, confirmStatus } = formData;

    // 1) 모드 설정: id 유무
    useEffect(() => {
        if (id) {
            setFormData((prev) => ({
                ...prev,
                mode: "UPDATE",
                id: id,
            }));
        }
    }, [id, setFormData]);

    // 2) UPDATE 모드일 경우, 보험 상세조회
    const {
        data: insuranceConfigDetail,
        isLoading: isDetailLoading,
        error: detailError,
    } = useGetInsuranceConfigDetailQuery(
        { rentCorpId: formData.rentCorpId, insuranceConfigId: id },
        {
            skip: !id || !formData.rentCorpId,
        }
    );

    // 렌터카 업체 목록 조회
    const { data: myRentCorpData, isLoading: isMyRentCorpLoading, error: myRentCorpError } = useGetMyRentCorpQuery();

    // corpOptions 생성
    const corpOptions = React.useMemo(() => {
        if (!myRentCorpData?.data) return [];
        return myRentCorpData.data.map((corp) => ({
            value: corp.id,
            label: corp.corpName,
        }));
    }, [myRentCorpData]);

    // 3) 상세조회 응답 → formData 매핑
    useEffect(() => {
        if (insuranceConfigDetail?.data) {
            const d = insuranceConfigDetail.data;
            setFormData((prev) => ({
                ...prev,
                name: d.name,
                defaultYn: d.defaultYn,
                useYn: d.useYn,
                contractTypes: d.contractTypes,
                insuranceConfigItems: d.insuranceConfigItems,
            }));
        }
    }, [insuranceConfigDetail, setFormData]);

    // 5) API mutations
    const [createInsuranceConfig, { isLoading: isCreateLoading }] = useCreateInsuranceConfigMutation();
    const [updateInsuranceConfig, { isLoading: isUpdateLoading }] = useUpdateInsuranceConfigMutation();
    const [deleteInsuranceConfig, { isLoading: isDeleteLoading }] = useDeleteInsuranceConfigMutation();

    // 6) 저장 Confirm
    const handleSaveConfirm = async () => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: { confirmPopupStatus: false },
        }));

        try {
            const requestBody = {
                name: formData.name,
                defaultYn: formData.defaultYn,
                useYn: formData.useYn,
                contractTypes: formData.contractTypes,
                insuranceConfigItems: formData.insuranceConfigItems,
            };

            if (mode === "UPDATE") {
                // 수정
                await updateInsuranceConfig({
                    rentCorpId: formData.rentCorpId,
                    insuranceConfigId: formData.id,
                    body: requestBody,
                }).unwrap();
            } else {
                // 등록
                await createInsuranceConfig({
                    rentCorpId: formData.rentCorpId,
                    body: requestBody,
                }).unwrap();
            }

            if (mode === "CREATE") {
                setFormData((prev) => ({
                    ...prev,
                    isLoading: false,
                    confirmStatus: {
                        confirmPopupStatus: true,
                        confirmTitle: "등록이 완료되었습니다.",
                        confirmDescription: "",
                        confirmButtons: [
                            {
                                label: "확인",
                                className: "btn-primary",
                                onClick: () => navigate("/admin/insurance-management"),
                            },
                        ],
                    },
                }));
            } else {
                setFormData((prev) => ({
                    ...prev,
                    toastMsg: "수정이 완료되었습니다.",
                    isLoading: false,
                }));
            }
        } catch (error) {
            console.error(error);
            setFormData((prev) => ({
                ...prev,
                toastMsg: error.message || "에러 발생",
            }));
        }
    };

    // 7) 저장 버튼
    const handleSave = () => {
        // 유효성 검사
        if (!formData.rentCorpId) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "사업자를 선택해주세요.",
            }));
            return;
        }

        if (!formData.name) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "보험 명칭을 입력해주세요.",
            }));
            return;
        }

        if (!formData.defaultYn) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "기본 보험 적용 여부를 선택해주세요.",
            }));
            return;
        }

        if (!formData.useYn) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "활성/비활성 여부를 선택해주세요.",
            }));
            return;
        }

        if (!formData.contractTypes || formData.contractTypes.length === 0) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "적용 계약 유형을 1개 이상 선택해주세요.",
            }));
            return;
        }

        setFormData((prev) => ({
            ...prev,
            confirmStatus: {
                confirmPopupStatus: true,
                confirmTitle: `보험 정보를 ${mode === "CREATE" ? "등록" : "수정"}하시겠습니까?`,
                confirmDescription: "",
                confirmButtons: [
                    { label: "예", className: "btn-primary", onClick: handleSaveConfirm },
                    {
                        label: "아니오",
                        className: "btn-cancel",
                        onClick: () =>
                            setFormData((prev2) => ({
                                ...prev2,
                                confirmStatus: { confirmPopupStatus: false },
                            })),
                    },
                ],
            },
        }));
    };

    // 8) 삭제 Confirm
    const handleDeleteConfirm = async () => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: { confirmPopupStatus: false },
        }));

        try {
            await deleteInsuranceConfig({
                rentCorpId: formData.rentCorpId,
                insuranceConfigId: formData.id,
            }).unwrap();

            setFormData((prev) => ({
                ...prev,
                confirmStatus: {
                    confirmPopupStatus: true,
                    confirmTitle: "삭제가 완료되었습니다.",
                    confirmDescription: "",
                    confirmButtons: [
                        {
                            label: "확인",
                            className: "btn-primary",
                            onClick: () => navigate("/admin/insurance-management"),
                        },
                    ],
                },
            }));
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error.message || "에러 발생",
            }));
        }
    };

    // 9) 삭제 버튼
    const handleDelete = () => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: {
                confirmPopupStatus: true,
                confirmTitle: "해당 보험 정보를 삭제하시겠습니까?",
                confirmDescription: "삭제 후에는 복구할 수 없습니다.",
                confirmButtons: [
                    { label: "예", className: "btn-primary", onClick: handleDeleteConfirm },
                    {
                        label: "아니오",
                        className: "btn-cancel",
                        onClick: () =>
                            setFormData((prev2) => ({
                                ...prev2,
                                confirmStatus: { confirmPopupStatus: false },
                            })),
                    },
                ],
            },
        }));
    };

    return (
        <>
            {toastMsg && <Toast visible={true} msg={toastMsg} setFormData={setFormData} />}

            <div className="management-form container">
                <div className="form-container">
                    <div className="form-head">
                        <div className="head-tit">
                            <span className="form_name">보험 {mode === "CREATE" ? "등록" : "수정"}</span>
                            <span className="form_desc"></span>
                        </div>
                        <div className="head-options">
                            {mode === "UPDATE" && (
                                <button type="button" className="btn btn-delete" onClick={handleDelete}>
                                    삭제
                                </button>
                            )}
                            <button type="button" className="btn btn-save" onClick={handleSave}>
                                저장
                            </button>
                        </div>
                    </div>

                    <div className="form-content">
                        <div className="form-wrapper">
                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">보험 정보</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <TermsForm
                                            formData={formData}
                                            handleChange={handleChange}
                                            setFormData={setFormData}
                                            corpOptions={corpOptions}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">보험 내용</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <InsuranceTable formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirm 팝업 */}
            {formData.confirmStatus.confirmPopupStatus && (
                <Confirm
                    layer={true}
                    onClose={() =>
                        setFormData((prev) => ({
                            ...prev,
                            confirmStatus: { confirmPopupStatus: false },
                        }))
                    }
                    title={formData.confirmStatus.confirmTitle}
                    description={formData.confirmStatus.confirmDescription}
                    buttons={formData.confirmStatus.confirmButtons}
                />
            )}
        </>
    );
};

/* 보험 정보 */
const TermsForm = ({ formData, handleChange, setFormData, corpOptions }) => {
    const { name, contractTypes, defaultYn, useYn } = formData;

    return (
        <>
            {/* 사업자 선택 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>사업자 선택</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={corpOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="사업자 선택"
                            value={corpOptions.find((opt) => opt.value === formData.rentCorpId)}
                            onChange={(option) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    rentCorpId: option.value,
                                }))
                            }
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3"></div>
            <div className="item-row" data-grow="3"></div>

            {/* 보험 명칭 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>보험 명칭</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="name" placeholder="보험 명칭" value={name || ""} onChange={handleChange} />
                    </div>
                </div>
            </div>

            {/* 기본 보험 적용 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>기본 보험 적용</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <div className="radiobox">
                            <input
                                type="radio"
                                name="defaultYn"
                                id="defaultYn_Y"
                                checked={defaultYn === "Y"}
                                onChange={(e) => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        defaultYn: "Y",
                                    }));
                                }}
                            />
                            <label htmlFor="defaultYn_Y">적용</label>
                        </div>
                        <div className="radiobox">
                            <input
                                type="radio"
                                name="defaultYn"
                                id="defaultYn_N"
                                checked={defaultYn === "N"}
                                onChange={(e) => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        defaultYn: "N",
                                    }));
                                }}
                            />
                            <label htmlFor="defaultYn_N">미적용</label>
                        </div>
                    </div>
                </div>
            </div>

            {/* 활성/비활성 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>활성/비활성</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <div className="radiobox">
                            <input
                                type="radio"
                                name="useYn"
                                id="useYn_Y"
                                checked={useYn === "Y"}
                                onChange={(e) => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        useYn: "Y",
                                    }));
                                }}
                            />
                            <label htmlFor="useYn_Y">활성화</label>
                        </div>
                        <div className="radiobox">
                            <input
                                type="radio"
                                name="useYn"
                                id="useYn_N"
                                checked={useYn === "N"}
                                onChange={(e) => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        useYn: "N",
                                    }));
                                }}
                            />
                            <label htmlFor="useYn_N">비활성화</label>
                        </div>
                    </div>
                </div>
            </div>

            {/* 적용 계약 유형 */}
            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>적용 계약 유형</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        {orderTypeOptions.map((option) => (
                            <div className="checkbox" key={option.value}>
                                <input
                                    type="checkbox"
                                    id={`contractType_${option.value}`}
                                    name="contractTypes"
                                    value={option.value}
                                    checked={contractTypes && contractTypes.includes(option.value)}
                                    onChange={(e) => {
                                        const { checked, value } = e.target;
                                        setFormData((prev) => {
                                            const updated = prev.contractTypes ? [...prev.contractTypes] : [];
                                            if (checked) {
                                                if (!updated.includes(value)) {
                                                    updated.push(value);
                                                }
                                            } else {
                                                const index = updated.indexOf(value);
                                                if (index > -1) {
                                                    updated.splice(index, 1);
                                                }
                                            }
                                            return {
                                                ...prev,
                                                contractTypes: updated,
                                            };
                                        });
                                    }}
                                />
                                <label htmlFor={`contractType_${option.value}`}>
                                    <span>{option.label}</span>
                                </label>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
};

/* 보험 정보 테이블 */
const InsuranceTable = ({ formData, handleChange, setFormData }) => {
    const insuranceConfigItems = formData?.insuranceConfigItems ?? [];
    const [selectAll, setSelectAll] = useState(false);

    // 초기 선택 상태 확인
    useEffect(() => {
        if (insuranceConfigItems.length > 0) {
            const allSelected = insuranceConfigItems.every((item) => item.joinYn === "Y");
            setSelectAll(allSelected);
        }
    }, [insuranceConfigItems]);

    // 특정 item 업데이트 헬퍼
    const updateItem = (index, newValues) => {
        setFormData((prev) => {
            const newInsuranceConfigItems = [...prev.insuranceConfigItems];
            newInsuranceConfigItems[index] = {
                ...newInsuranceConfigItems[index],
                ...newValues,
            };
            return {
                ...prev,
                insuranceConfigItems: newInsuranceConfigItems,
            };
        });
    };

    // joinYn 토글 핸들러
    const handleToggleJoinYn = (index, currentJoinYn) => {
        const nextJoinYn = currentJoinYn === "Y" ? "N" : "Y";
        updateItem(index, { joinYn: nextJoinYn });

        // 모든 항목이 선택되었는지 확인
        const allSelected = insuranceConfigItems.every((item) => item.joinYn === nextJoinYn);
        setSelectAll(allSelected);
    };

    // 전체 선택 토글 핸들러
    const handleToggleAll = () => {
        const nextSelectAll = !selectAll;
        setSelectAll(nextSelectAll);

        // 모든 항목의 joinYn을 업데이트
        setFormData((prev) => ({
            ...prev,
            insuranceConfigItems: prev.insuranceConfigItems.map((item) => ({
                ...item,
                joinYn: nextSelectAll ? "Y" : "N",
            })),
        }));
    };

    return (
        <>
            <div className="table-container">
                <div className="table-content">
                    <div className="table-wrapper">
                        <div className="table">
                            <table>
                                <colgroup>
                                    <col width="12%" />
                                    <col width="25%" />
                                    <col width="25%" />
                                    <col width="25%" />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th>
                                            <div className="cell">
                                                <span
                                                    className="cell-tit"
                                                    style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px" }}
                                                >
                                                    가입 여부{" "}
                                                    <button
                                                        type="button"
                                                        className={`btn-toggle-own ${selectAll ? "active" : ""}`}
                                                        onClick={handleToggleAll}
                                                    />
                                                </span>
                                            </div>
                                        </th>
                                        <th>
                                            <div className="cell">
                                                <span className="cell-tit">종류</span>
                                            </div>
                                        </th>
                                        <th>
                                            <div className="cell">
                                                <span className="cell-tit">보상범위</span>
                                            </div>
                                        </th>
                                        <th>
                                            <div className="cell">
                                                <span className="cell-tit">자부담금</span>
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {insuranceConfigItems.map((item, idx) => {
                                        const config = INSURANCE_ITEM_CONFIGS[item.insuranceItemType];
                                        if (!config) {
                                            return null;
                                        }

                                        return (
                                            <tr key={item.insuranceItemType}>
                                                {/* 가입 여부 버튼 */}
                                                <td>
                                                    <div className="cell" data-align="center">
                                                        <button
                                                            type="button"
                                                            className={`btn-toggle-own ${item.joinYn === "Y" ? "active" : ""}`}
                                                            onClick={() => handleToggleJoinYn(idx, item.joinYn)}
                                                        />
                                                    </div>
                                                </td>

                                                {/* 보험 종류 라벨 */}
                                                <td>
                                                    <div className="cell" data-align="center">
                                                        <span className="cell-desc color-main">{config.label}</span>
                                                    </div>
                                                </td>

                                                {/* 보상범위 Select 영역 */}
                                                <td>
                                                    <div className="cell" data-align="center">
                                                        <span className="cell-input">
                                                            {item.insuranceItemType === "SELF_CAR_DAMAGE" ? (
                                                                <>
                                                                    <Select
                                                                        className="custom-select"
                                                                        classNamePrefix="custom-select"
                                                                        isSearchable={false}
                                                                        placeholder="구분"
                                                                        value={
                                                                            item.selfCarDamageRangeType
                                                                                ? selfCarDamageTypeOptions.find(
                                                                                      (opt) => opt.value === item.selfCarDamageRangeType
                                                                                  )
                                                                                : null
                                                                        }
                                                                        onChange={(option) => {
                                                                            updateItem(idx, {
                                                                                selfCarDamageRangeType: option.value,
                                                                                compensationRange: null,
                                                                            });
                                                                        }}
                                                                        options={selfCarDamageTypeOptions}
                                                                    />

                                                                    {item.selfCarDamageRangeType === "PRICE" && (
                                                                        <Select
                                                                            className="custom-select"
                                                                            classNamePrefix="custom-select"
                                                                            isSearchable={false}
                                                                            placeholder="금액"
                                                                            value={
                                                                                item.compensationRange
                                                                                    ? selfCarDamageAmountOptions.find(
                                                                                          (opt) => opt.value === item.compensationRange
                                                                                      )
                                                                                    : null
                                                                            }
                                                                            onChange={(option) => {
                                                                                updateItem(idx, {
                                                                                    compensationRange: option.value,
                                                                                });
                                                                            }}
                                                                            options={selfCarDamageAmountOptions}
                                                                        />
                                                                    )}

                                                                    {item.selfCarDamageRangeType === "RATE" && (
                                                                        <Select
                                                                            className="custom-select"
                                                                            classNamePrefix="custom-select"
                                                                            isSearchable={false}
                                                                            placeholder="비율"
                                                                            value={
                                                                                item.selfCarDamageRateType
                                                                                    ? selfCarDamageRateOptions.find(
                                                                                          (opt) => opt.value === item.selfCarDamageRateType
                                                                                      )
                                                                                    : null
                                                                            }
                                                                            onChange={(option) => {
                                                                                updateItem(idx, {
                                                                                    selfCarDamageRateType: option.value,
                                                                                });
                                                                            }}
                                                                            options={selfCarDamageRateOptions}
                                                                        />
                                                                    )}
                                                                </>
                                                            ) : item.insuranceItemType === "DAEIN_PAY_1" ? (
                                                                <Select
                                                                    className="custom-select"
                                                                    classNamePrefix="custom-select"
                                                                    isSearchable={false}
                                                                    options={config.coverageOptions}
                                                                    placeholder="보상범위"
                                                                    value={
                                                                        item.limitType
                                                                            ? config.coverageOptions.find(
                                                                                  (opt) => opt.value === item.limitType
                                                                              )
                                                                            : null
                                                                    }
                                                                    onChange={(option) => {
                                                                        updateItem(idx, {
                                                                            limitType:
                                                                                typeof option.value === "string" ? option.value : null,
                                                                            compensationRange:
                                                                                typeof option.value === "number" ? option.value : null,
                                                                        });
                                                                    }}
                                                                />
                                                            ) : item.insuranceItemType === "DAEIN_PAY_2" ? (
                                                                <Select
                                                                    className="custom-select"
                                                                    classNamePrefix="custom-select"
                                                                    isSearchable={false}
                                                                    options={config.coverageOptions}
                                                                    placeholder="보상범위"
                                                                    value={
                                                                        item.limitType === "UN_LIMIT"
                                                                            ? config.coverageOptions.find((opt) => opt.value === "UN_LIMIT")
                                                                            : config.coverageOptions.find(
                                                                                  (opt) => opt.value === item.compensationRange
                                                                              )
                                                                    }
                                                                    onChange={(option) => {
                                                                        if (option.value === "UN_LIMIT") {
                                                                            updateItem(idx, {
                                                                                limitType: "UN_LIMIT",
                                                                                compensationRange: null,
                                                                            });
                                                                        } else {
                                                                            updateItem(idx, {
                                                                                limitType: null,
                                                                                compensationRange: Number(option.value),
                                                                            });
                                                                        }
                                                                    }}
                                                                />
                                                            ) : item.insuranceItemType === "EMERGENCY_SERVICE" ? (
                                                                <Select
                                                                    className="custom-select"
                                                                    classNamePrefix="custom-select"
                                                                    isSearchable={false}
                                                                    options={config.coverageOptions}
                                                                    placeholder="연 횟수"
                                                                    value={
                                                                        item.compensationRange
                                                                            ? config.coverageOptions.find(
                                                                                  (opt) => opt.value === item.compensationRange
                                                                              )
                                                                            : null
                                                                    }
                                                                    onChange={(option) => {
                                                                        updateItem(idx, {
                                                                            limitType: null,
                                                                            compensationRange: option.value,
                                                                        });
                                                                    }}
                                                                />
                                                            ) : (
                                                                <Select
                                                                    className="custom-select"
                                                                    classNamePrefix="custom-select"
                                                                    isSearchable={false}
                                                                    options={config.coverageOptions}
                                                                    placeholder="보상범위"
                                                                    value={
                                                                        item.compensationRange
                                                                            ? config.coverageOptions.find(
                                                                                  (opt) => opt.value === item.compensationRange
                                                                              )
                                                                            : null
                                                                    }
                                                                    onChange={(option) => {
                                                                        updateItem(idx, {
                                                                            limitType: null,
                                                                            compensationRange: Number(option.value),
                                                                        });
                                                                    }}
                                                                />
                                                            )}
                                                        </span>
                                                    </div>
                                                </td>

                                                {/* 자부담금 입력 */}
                                                <td>
                                                    <div className="cell" data-align="left">
                                                        <span className="cell-input">
                                                            <input
                                                                type="number"
                                                                placeholder="0"
                                                                value={item.selfPay || ""}
                                                                onChange={(e) => {
                                                                    const val = e.target.value;
                                                                    updateItem(idx, {
                                                                        selfPay: val ? Number(val) : null,
                                                                    });
                                                                }}
                                                            />
                                                            <span className="input-esset">
                                                                {item.insuranceItemType === "EMERGENCY_SERVICE" ? "km" : "원"}
                                                            </span>
                                                        </span>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
